/**
 * Worker expense receipts — the one place that knows the `/expenses` payload.
 *
 * CONTRACT — verified against fieldiq-api `expenses.routes.ts` at 707ac7d (#514)
 * ------------------------------------------------------------------------------
 * Rows are snake_case: `gst_amount`, `rejected_reason`, `worker_name`,
 * `job_number`, `receipt_upload_id`, and once approved `pay_period_id`,
 * `period_start`, `period_end`, `reflected_in_period_total`,
 * `period_total_note`. Writes: `job_id`, `vendor`, `expense_date`, `amount`,
 * `gst_amount`, `category`, `description`, `receipt_upload_id`. `ocr_raw` is
 * server-side only and a body carrying it is ignored.
 *
 * The receipt is presigned as `expense_receipt` with entityId = the JOB id
 * (uploads.entity_id is NOT NULL and the expense does not exist yet); POST
 * /expenses re-points it. OCR takes `{receipt_upload_id}` and runs on the
 * stored bytes. Approve answers `data.pay_period` (the period it landed on),
 * `data.carried_forward`, `data.message`, and `data.period_total_note` — the
 * header does not move until the office runs Generate + Save on that period.
 *
 * Reads still tolerate camelCase because that is the house rule, not because
 * this route sends any.
 *
 * THE TICKET (api #537, migration 0370)
 * -------------------------------------
 * `ticket_id` is OPTIONAL on every write path and NULL on most rows: a receipt
 * is photographed before the day's ticket exists, so requiring one would make
 * the common case unfileable. `job_id` stays required and is unchanged.
 *
 * THE PAIR MUST AGREE. `tickets.job_id` is NOT NULL, so a ticket names exactly
 * one job, and the API refuses a ticket from a different job with a 400 naming
 * both. That is why the picker is fed from `GET /tickets?jobId=` for the
 * expense's OWN job and why changing the job clears the choice — offering a
 * ticket the API will refuse is a 400 the user cannot act on.
 *
 * Rows read back carry `ticket_number` (the office needs the number, not the
 * uuid) and `ticket_deleted` — NULL when there is no ticket at all, true when
 * the linked ticket has since been soft-deleted. The read join is deliberately
 * not filtered on `deleted_at`, so the number survives and the flag reports it.
 *
 * Approve takes an optional `ticket_id` and applies it only while the expense
 * is still pending. A re-approve stays idempotent — never a 409 on a live money
 * path — and REPORTS that the ticket was not applied via `ticket_id_applied`
 * and `ticket_id_note`. Both are surfaced; dropping them is how the office
 * comes to believe a ticket is attached when it is not.
 *
 * WHO PAID (api #540, migration 0372)
 * ------------------------------------
 * `paid_by` is `'personal'` (the worker is owed it back) or `'company'` (the
 * company card already paid, and nothing is owed). It is OPTIONAL on every
 * write — the column defaults to `'personal'` — but this screen always sends
 * it, because the hand is asked outright and an answer given should be an
 * answer stated. It is editable only while pending; the API's status gate
 * refuses it afterwards, which is right: by then the money, or the deliberate
 * absence of it, is already recorded.
 *
 * `payroll_path` is the API SAYING WHICH BRANCH RAN — `'reimbursement'` or
 * `'none'` — and it is on every serialize(), not just approve. It exists
 * because a company-paid approval leaves `payroll_adjustment_id` NULL and
 * `reflected_in_period_total` NULL, which is exactly what a FAILED approval
 * would also look like. Three states rendering identically is the
 * silent-blank-field family; the API names the case in `period_total_note`
 * and `payroll_path`, and both are surfaced verbatim rather than inferred
 * from the null.
 *
 * Neither is defaulted on READ. An absent `paid_by` means the payload did not
 * say — `—`, not `'personal'`. Defaulting it here would render a field the
 * API never sent as a decision someone made.
 *
 * ABSENT IS NOT ZERO
 * ------------------
 * `amount` and `gst` are `number | null`, never defaulted. A receipt whose
 * total could not be read renders as `—` (`fmtCurrency` does that); a `0`
 * would look like a decision someone made. The amount that reaches payroll is
 * whatever the API says it is — nothing here subtracts GST, sums, or rounds.
 */

import { unwrap, unwrapArray } from './api-envelope';
import { parseMoney } from './display-labels';
import { calendarDayString } from './calendar-date';

export type ExpenseStatus = 'pending' | 'approved' | 'rejected';

export const EXPENSE_STATUSES: readonly ExpenseStatus[] = ['pending', 'approved', 'rejected'];

export const EXPENSE_STATUS_LABELS: Record<ExpenseStatus, string> = {
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
};

/** Tolerates an unknown status rather than rendering blank. */
export function getExpenseStatusLabel(status: string | null | undefined): string {
  if (!status) return '—';
  return EXPENSE_STATUS_LABELS[status as ExpenseStatus]
    ?? status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// ── Who paid, and what payroll did about it (api #540, migration 0372) ──────

export type PaidBy = 'personal' | 'company';

export const PAID_BY_VALUES: readonly PaidBy[] = ['personal', 'company'];

/**
 * Exhaustive, keyed on every value the CHECK allows — never a binary test.
 * The wording is the hand's question, not the column name: a hand asked "who
 * paid?" answers "me" or "the company card", and "Personal" alone reads as a
 * category of expense rather than as whose money it was.
 */
export const PAID_BY_LABELS: Record<PaidBy, string> = {
  personal: 'Personal',
  company: 'Company card',
};

/** What choosing it means, in the one sentence the person acting on it needs. */
export const PAID_BY_CONSEQUENCE: Record<PaidBy, string> = {
  personal: 'You paid out of pocket — approved receipts are reimbursed on your pay.',
  company: 'Paid on a company card — recorded against the job for cost, and never reimbursed.',
};

export function getPaidByLabel(value: string | null | undefined): string {
  if (!value) return '—';
  return PAID_BY_LABELS[value as PaidBy]
    ?? value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function toPaidBy(value: unknown): PaidBy | null {
  if (typeof value !== 'string') return null;
  const key = value.trim().toLowerCase();
  return (PAID_BY_VALUES as readonly string[]).includes(key) ? (key as PaidBy) : null;
}

/**
 * Which branch the approval ran, as the API states it.
 *
 * `'none'` is a RESULT, not an absence. It is what a company-paid approval
 * means and it must never render as a blank or as a failed reimbursement —
 * the whole reason the API sends the word instead of leaving a null to be
 * read.
 */
export type PayrollPath = 'reimbursement' | 'report' | 'none';

export const PAYROLL_PATH_LABELS: Record<PayrollPath, string> = {
  reimbursement: 'Reimbursement',
  // API-1b (0502): paid back outside payroll, on the reimbursement report.
  report: 'Reimbursement report',
  none: 'No reimbursement',
};

/**
 * What a receipt was for (API-1, 0501). 'job' is the API's column default and
 * every caller written before 0501; 'general' has NO job — a sales lunch — and
 * only the office tree offers it (D2).
 */
export type ExpenseKind = 'job' | 'general';

export const EXPENSE_KIND_LABELS: Record<ExpenseKind, string> = {
  job: 'On a job',
  general: 'General / sales',
};

export function getPayrollPathLabel(value: string | null | undefined): string {
  if (!value) return '—';
  return PAYROLL_PATH_LABELS[value as PayrollPath]
    ?? value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * What a hand buys on a job. An OCR suggestion outside this list is shown as
 * text but never written to the category field — the API's CHECK is the gate,
 * and a value it rejects would 400 the whole submission.
 */
export const EXPENSE_CATEGORIES = [
  'fuel',
  'meals',
  'lodging',
  'supplies',
  'parts',
  'tools',
  'travel',
  'other',
] as const;

export type ExpenseCategory = (typeof EXPENSE_CATEGORIES)[number];

export const EXPENSE_CATEGORY_LABELS: Record<ExpenseCategory, string> = {
  fuel: 'Fuel',
  meals: 'Meals',
  lodging: 'Lodging',
  supplies: 'Supplies',
  parts: 'Parts',
  tools: 'Tools',
  travel: 'Travel',
  other: 'Other',
};

export function getExpenseCategoryLabel(category: string | null | undefined): string {
  if (!category) return '—';
  return EXPENSE_CATEGORY_LABELS[category as ExpenseCategory]
    ?? category.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Case-insensitive match into the list; anything else is null, not 'other'. */
export function toExpenseCategory(value: unknown): ExpenseCategory | null {
  if (typeof value !== 'string') return null;
  const key = value.trim().toLowerCase();
  return (EXPENSE_CATEGORIES as readonly string[]).includes(key) ? (key as ExpenseCategory) : null;
}

export interface PayPeriodRef {
  id: string | null;
  /** Calendar days (YYYY-MM-DD), not instants. */
  start: string | null;
  end: string | null;
}

export interface Expense {
  id: string;
  status: string;
  vendor: string | null;
  /** Calendar day the receipt is dated, YYYY-MM-DD. */
  expenseDate: string | null;
  amount: number | null;
  gst: number | null;
  category: string | null;
  description: string | null;
  jobId: string | null;
  jobNumber: string | null;
  /** 'job' | 'general' (0501). Null when the payload did not say — not defaulted. */
  expenseKind: string | null;
  /**
   * The customer, by name: the expense's own, else its job's (the API's
   * `customer_name` is the EFFECTIVE customer). Null renders as '—'.
   */
  customerName: string | null;
  /** The ticket this receipt was incurred on, when the office knows it. */
  ticketId: string | null;
  ticketNumber: string | null;
  /**
   * Whether the linked ticket has since been soft-deleted. `null` when there
   * is no ticket at all — the API distinguishes the two and so does this, so a
   * row with no ticket never reads as a row whose ticket was deleted.
   */
  ticketDeleted: boolean | null;
  /**
   * Whose money paid for it — `'personal'` or `'company'` (0372). NOT
   * defaulted: `null` is "the payload did not say", which is a different fact
   * from "the worker paid" and must not be dressed up as one.
   */
  paidBy: string | null;
  /**
   * The API's own word for what payroll did — `'reimbursement'` or `'none'`.
   * Present on every row, not only approved ones. Read, never derived: a
   * company-paid row's NULL adjustment is indistinguishable from a failure,
   * and this is the field that tells the two apart.
   */
  payrollPath: string | null;
  workerId: string | null;
  workerName: string | null;
  /** `uploads.id` of the receipt photo — the id `/uploads/download/:id` takes. */
  receiptUploadId: string | null;
  rejectionReason: string | null;
  /** The pay period the reimbursement landed on, once approved. */
  payPeriod: PayPeriodRef | null;
  /** True when the API put the adjustment on a later period than the receipt's. */
  carriedForward: boolean;
  /**
   * Whether the period header already carries this money. NULL until there is
   * an adjustment; false until the office runs Generate + Save on that period.
   * Reported by the API, never derived here.
   */
  reflectedInPeriodTotal: boolean | null;
  periodTotalNote: string | null;
  /**
   * The path 0502 STAMPED at approval: 'payroll' | 'report' | 'none'. Null
   * while pending or rejected. Read, never re-derived from the person's current
   * setting — history does not move when a setting changes.
   */
  reimbursementPath: string | null;
  /** When a report-path expense was marked paid back outside payroll (instant). */
  reimbursedAt: string | null;
  /** The API's own word: report + approved + not yet reimbursed. */
  reimbursementOwed: boolean | null;
  createdAt: string | null;
}

const str = (v: unknown): string | null =>
  typeof v === 'string' && v.trim() !== '' ? v : typeof v === 'number' ? String(v) : null;

/** First present key wins; `undefined` and `null` both count as absent. */
function pick(raw: Record<string, unknown>, ...keys: string[]): unknown {
  for (const k of keys) {
    const v = raw[k];
    if (v !== undefined && v !== null) return v;
  }
  return undefined;
}

function calendarDay(v: unknown): string | null {
  const s = str(v);
  return s ? (calendarDayString(s) ?? s) : null;
}

function workerNameOf(raw: Record<string, unknown>): string | null {
  const direct = str(pick(raw, 'workerName', 'worker_name', 'userName', 'user_name', 'employeeName', 'employee_name'));
  if (direct) return direct;
  const user = (pick(raw, 'user', 'worker', 'employee') ?? {}) as Record<string, unknown>;
  const first = str(pick(user, 'firstName', 'first_name')) ?? str(pick(raw, 'firstName', 'first_name'));
  const last = str(pick(user, 'lastName', 'last_name')) ?? str(pick(raw, 'lastName', 'last_name'));
  const joined = [first, last].filter(Boolean).join(' ');
  return joined || null;
}

/**
 * A period object that arrived under a `payPeriod` / `pay_period` key: its
 * own `id` IS the period id.
 */
function periodFromBare(r: Record<string, unknown>): PayPeriodRef | null {
  const id = str(pick(r, 'payPeriodId', 'pay_period_id', 'id'));
  const start = calendarDay(pick(r, 'payPeriodStart', 'pay_period_start', 'periodStart', 'period_start', 'startDate', 'start_date', 'start'));
  const end = calendarDay(pick(r, 'payPeriodEnd', 'pay_period_end', 'periodEnd', 'period_end', 'endDate', 'end_date', 'end'));
  if (!id && !start && !end) return null;
  return { id, start, end };
}

/**
 * Period columns flattened onto some OTHER row (an expense, an adjustment).
 * That row's bare `id` is the row's id, never the period's, so only the
 * prefixed spellings count here.
 */
function periodFromPrefixed(r: Record<string, unknown>): PayPeriodRef | null {
  const id = str(pick(r, 'payPeriodId', 'pay_period_id'));
  const start = calendarDay(pick(r, 'payPeriodStart', 'pay_period_start', 'periodStart', 'period_start'));
  const end = calendarDay(pick(r, 'payPeriodEnd', 'pay_period_end', 'periodEnd', 'period_end'));
  if (!id && !start && !end) return null;
  return { id, start, end };
}

/** Accepts an expense row, an adjustment row, or a bare period under either. */
export function parsePayPeriodRef(raw: unknown): PayPeriodRef | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const nested = pick(r, 'payPeriod', 'pay_period', 'period');
  if (nested && typeof nested === 'object') return periodFromBare(nested as Record<string, unknown>);
  return periodFromPrefixed(r);
}

export function parseExpense(raw: unknown): Expense | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const id = str(r.id);
  if (!id) return null;
  const job = (pick(r, 'job') ?? {}) as Record<string, unknown>;
  const ticket = (pick(r, 'ticket') ?? {}) as Record<string, unknown>;
  const receipt = (pick(r, 'receipt', 'receiptUpload', 'receipt_upload') ?? {}) as Record<string, unknown>;
  const carried = pick(r, 'carriedForward', 'carried_forward', 'carryForward', 'carry_forward');
  return {
    id,
    status: str(r.status) ?? 'pending',
    vendor: str(pick(r, 'vendor', 'vendorName', 'vendor_name')),
    expenseDate: calendarDay(pick(r, 'expenseDate', 'expense_date', 'receiptDate', 'receipt_date', 'date')),
    amount: parseMoney(pick(r, 'amount', 'total', 'totalAmount', 'total_amount')),
    gst: parseMoney(pick(r, 'gst', 'gstAmount', 'gst_amount', 'tax', 'taxAmount', 'tax_amount')),
    category: str(pick(r, 'category')),
    description: str(pick(r, 'description', 'notes', 'note')),
    jobId: str(pick(r, 'jobId', 'job_id')) ?? str(job.id),
    jobNumber: str(pick(r, 'jobNumber', 'job_number')) ?? str(pick(job, 'jobNumber', 'job_number')),
    expenseKind: str(pick(r, 'expenseKind', 'expense_kind')),
    customerName: str(pick(r, 'customerName', 'customer_name')),
    ticketId: str(pick(r, 'ticketId', 'ticket_id')) ?? str(ticket.id),
    ticketNumber: str(pick(r, 'ticketNumber', 'ticket_number')) ?? str(pick(ticket, 'ticketNumber', 'ticket_number')),
    ticketDeleted: (() => {
      const v = pick(r, 'ticketDeleted', 'ticket_deleted');
      return v === true ? true : v === false ? false : null;
    })(),
    paidBy: str(pick(r, 'paidBy', 'paid_by')),
    payrollPath: str(pick(r, 'payrollPath', 'payroll_path')),
    workerId: str(pick(r, 'userId', 'user_id', 'employeeId', 'employee_id', 'workerId', 'worker_id')),
    workerName: workerNameOf(r),
    receiptUploadId: str(pick(r, 'receiptUploadId', 'receipt_upload_id', 'uploadId', 'upload_id')) ?? str(receipt.id),
    rejectionReason: str(pick(r, 'rejectedReason', 'rejected_reason', 'rejectionReason', 'rejection_reason')),
    payPeriod: parsePayPeriodRef(pick(r, 'adjustment', 'payrollAdjustment', 'payroll_adjustment') ?? r),
    carriedForward: carried === true || carried === 'true' || carried === 1,
    reflectedInPeriodTotal: (() => {
      const v = pick(r, 'reflectedInPeriodTotal', 'reflected_in_period_total');
      return v === true ? true : v === false ? false : null;
    })(),
    periodTotalNote: str(pick(r, 'periodTotalNote', 'period_total_note')),
    reimbursementPath: str(pick(r, 'reimbursementPath', 'reimbursement_path')),
    reimbursedAt: str(pick(r, 'reimbursedAt', 'reimbursed_at')),
    reimbursementOwed: (() => {
      const v = pick(r, 'reimbursementOwed', 'reimbursement_owed');
      return v === true ? true : v === false ? false : null;
    })(),
    createdAt: str(pick(r, 'createdAt', 'created_at')),
  };
}

export function parseExpenseList(body: unknown): Expense[] {
  const payload = unwrap<unknown>(body);
  const rows = Array.isArray(payload)
    ? payload
    : payload && typeof payload === 'object' && Array.isArray((payload as any).expenses)
      ? (payload as any).expenses
      : [];
  return rows.map(parseExpense).filter((e: Expense | null): e is Expense => e !== null);
}

/**
 * What `POST /expenses/:id/approve` answers.
 *
 * The number the office will be asked about is WHICH pay period the
 * reimbursement landed on. When the receipt's own period is already closed the
 * API carries the adjustment forward to the next open one, and the approver
 * has to be told so — a hand asking "why isn't it on this cheque" is the
 * whole reason the flag exists. Read from the adjustment when there is one,
 * from the expense when there is not, and never invent a period.
 */
export interface ApproveResult {
  expense: Expense | null;
  payPeriod: PayPeriodRef | null;
  carriedForward: boolean;
  /** The API's own sentence about where the money went; shown verbatim when present. */
  message: string | null;
  reflectedInPeriodTotal: boolean | null;
  periodTotalNote: string | null;
  /**
   * Whether a `ticket_id` sent with the approve was actually applied.
   *
   * `null` — none was sent, so there is nothing to report.
   * `true` — applied; the expense was still pending.
   * `false` — NOT applied. The expense was already approved, so the write
   *           branch never ran. This is the one that must reach the screen:
   *           a silent drop is how the office comes to believe a ticket is
   *           attached when it is not.
   */
  ticketIdApplied: boolean | null;
  /** The API's own sentence about the above; shown verbatim when present. */
  ticketIdNote: string | null;
  /** Whose money paid — echoed by both approve branches (0372). */
  paidBy: string | null;
  /**
   * WHICH BRANCH RAN, in the API's own word: `'reimbursement'` or `'none'`.
   *
   * The company branch writes no payroll_adjustments row, so `adjustment`,
   * `pay_period` and `reflectedInPeriodTotal` all come back null — and that is
   * also what a failure looks like. The API states the path rather than
   * leaving it to be inferred, so it is read here rather than derived from the
   * nulls.
   */
  payrollPath: string | null;
  /** api#1024 D13: the sole active admin approved their own expense. Stated by the API. */
  selfApproved: boolean;
}

export function parseApproveResult(body: unknown): ApproveResult {
  const empty: ApproveResult = { expense: null, payPeriod: null, carriedForward: false, message: null, reflectedInPeriodTotal: null, periodTotalNote: null, ticketIdApplied: null, ticketIdNote: null, paidBy: null, payrollPath: null, selfApproved: false };
  const payload = unwrap<unknown>(body);
  if (!payload || typeof payload !== 'object') return empty;
  const p = payload as Record<string, unknown>;
  const adjustment = pick(p, 'adjustment', 'payrollAdjustment', 'payroll_adjustment') as Record<string, unknown> | undefined;
  const expenseRaw = pick(p, 'expense') ?? p;
  const expense = parseExpense(expenseRaw);
  const carriedRaw =
    pick(p, 'carriedForward', 'carried_forward', 'carryForward', 'carry_forward') ??
    (adjustment && pick(adjustment, 'carriedForward', 'carried_forward', 'carryForward', 'carry_forward'));
  const carriedForward = carriedRaw === true || carriedRaw === 'true' || carriedRaw === 1 || (expense?.carriedForward ?? false);
  // `data.pay_period` is the period it landed on and carries its dates; the
  // adjustment row only carries `pay_period_id`, so it is the fallback, not
  // the first read — an id with no dates would win over dates otherwise.
  const nested = pick(p, 'payPeriod', 'pay_period');
  const payPeriod =
    (nested && typeof nested === 'object' ? parsePayPeriodRef({ pay_period: nested }) : null) ??
    expense?.payPeriod ??
    (adjustment ? parsePayPeriodRef(adjustment) : null) ??
    null;
  const reflectedRaw = pick(p, 'reflectedInPeriodTotal', 'reflected_in_period_total');
  const appliedRaw = pick(p, 'ticketIdApplied', 'ticket_id_applied');
  return {
    expense,
    payPeriod,
    carriedForward,
    message: str(pick(p, 'message')),
    reflectedInPeriodTotal: reflectedRaw === true ? true : reflectedRaw === false ? false : expense?.reflectedInPeriodTotal ?? null,
    periodTotalNote: str(pick(p, 'periodTotalNote', 'period_total_note')) ?? expense?.periodTotalNote ?? null,
    ticketIdApplied: appliedRaw === true ? true : appliedRaw === false ? false : null,
    ticketIdNote: str(pick(p, 'ticketIdNote', 'ticket_id_note')),
    // Top level first — both branches state it there — then the serialized
    // expense, which carries the same two fields. Never derived from the null
    // adjustment: that null is also what a failure produces.
    paidBy: str(pick(p, 'paidBy', 'paid_by')) ?? expense?.paidBy ?? null,
    payrollPath: str(pick(p, 'payrollPath', 'payroll_path')) ?? expense?.payrollPath ?? null,
    selfApproved: pick(p, 'selfApproved', 'self_approved') === true,
  };
}

// ── OCR ─────────────────────────────────────────────────────────────────────

export interface OcrPrefill {
  vendor: string | null;
  /** Calendar day, YYYY-MM-DD. */
  date: string | null;
  amount: number | null;
  gst: number | null;
  /** The suggestion as the provider phrased it, whether or not it maps. */
  categorySuggestion: string | null;
  /** The suggestion mapped into EXPENSE_CATEGORIES, or null. */
  category: ExpenseCategory | null;
  /** What the scan could not vouch for — "no total was read", a sum that does not add up. */
  warnings: string[];
  raw: unknown;
}

/**
 * The three ways a scan ends, and they get three different sentences:
 *
 *   ok          — fields to prefill; the hand still edits and confirms.
 *   unreadable  — a 200 carrying `error: true`. The provider looked and could
 *                 not read the image. The fix is a better photo: retake.
 *   outage      — a 502. The provider is down. No photo will help; enter it by
 *                 hand. Nothing about the receipt is wrong.
 *   failed      — anything else (network, 500, a 4xx on our side). Manual
 *                 entry is offered without blaming the photo.
 *
 * Manual entry is available in every branch. The feature must not depend on a
 * scan succeeding, so the form is never gated behind one.
 */
export type OcrOutcome =
  | { kind: 'ok'; prefill: OcrPrefill }
  | { kind: 'unreadable'; message: string | null }
  | { kind: 'outage' }
  | { kind: 'failed'; message: string | null };

function calendarFromOcr(v: unknown): string | null {
  const s = str(v);
  if (!s) return null;
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[1]}-${m[2]}-${m[3]}` : null;
}

export function parseOcrPrefill(payload: unknown, warnings: unknown = []): OcrPrefill {
  const r = (payload && typeof payload === 'object' ? payload : {}) as Record<string, unknown>;
  const suggestion = str(pick(r, 'category_suggestion', 'categorySuggestion', 'category'));
  return {
    warnings: Array.isArray(warnings) ? warnings.filter((w): w is string => typeof w === 'string') : [],
    vendor: str(pick(r, 'vendor', 'vendorName', 'vendor_name', 'merchant')),
    date: calendarFromOcr(pick(r, 'date', 'expense_date', 'expenseDate', 'receipt_date', 'receiptDate')),
    amount: parseMoney(pick(r, 'total', 'amount', 'total_amount', 'totalAmount')),
    gst: parseMoney(pick(r, 'gst', 'gst_amount', 'gstAmount', 'tax', 'tax_amount', 'taxAmount')),
    categorySuggestion: suggestion,
    category: toExpenseCategory(suggestion),
    raw: payload ?? null,
  };
}

/** A resolved response (any 2xx). */
export function classifyOcrResponse(body: unknown): OcrOutcome {
  const b = (body && typeof body === 'object' ? body : {}) as Record<string, unknown>;
  const inner = (b.data && typeof b.data === 'object' ? b.data : {}) as Record<string, unknown>;
  if (b.error === true || inner.error === true || b.success === false) {
    return {
      kind: 'unreadable',
      message: str(pick(b, 'message', 'reason')) ?? str(pick(inner, 'message', 'reason')) ?? (typeof b.error === 'string' ? b.error : null),
    };
  }
  const payload = unwrap<unknown>(body);
  if (!payload || typeof payload !== 'object') return { kind: 'failed', message: null };
  return { kind: 'ok', prefill: parseOcrPrefill(payload, b.warnings) };
}

/** A rejected request (axios error). */
export function classifyOcrError(err: unknown): OcrOutcome {
  const status = (err as any)?.response?.status;
  if (status === 502 || status === 503 || status === 504) return { kind: 'outage' };
  const body = (err as any)?.response?.data;
  // A refusal the provider phrased as a 4xx with `error: true` is still "your
  // photo, not our provider": say retake.
  if (status && status < 500 && body && typeof body === 'object' && (body as any).error === true) {
    return { kind: 'unreadable', message: str((body as any).message) };
  }
  const message = body && typeof body === 'object' && typeof (body as any).error === 'string' ? (body as any).error : null;
  return { kind: 'failed', message };
}

export const OCR_MESSAGES = {
  unreadable: "We couldn't read this receipt. Retake the photo in better light, or enter the details below.",
  outage: 'Receipt scanning is unavailable right now. Enter the details below — the photo is still attached.',
  failed: "The receipt couldn't be scanned. Enter the details below — the photo is still attached.",
} as const;

// ── Writes ──────────────────────────────────────────────────────────────────

/** Snake_case body for POST /expenses. Numbers are numbers, absent is omitted. */
export interface ExpenseCreateBody {
  /**
   * Required on a job receipt; ABSENT — the key, not just the value — on a
   * general one (0501). The API reads any job_id on a general expense as a 400.
   */
  job_id?: string;
  /** Omitted for a job receipt (the API's default); 'general' otherwise. */
  expense_kind?: ExpenseKind;
  /** Optional, general receipts only from this app. Omitted when none is chosen. */
  customer_id?: string;
  /**
   * Who paid (0372). Optional to the API — the column defaults to 'personal' —
   * but always sent from here: the hand is asked outright, so the answer is
   * stated rather than left to a default that happens to agree with it.
   */
  paid_by: PaidBy;
  vendor: string;
  expense_date: string;
  amount: number;
  gst_amount?: number;
  category: ExpenseCategory;
  description?: string;
  /** `uploads.id` of the receipt row — the API re-points it from the job to the expense. */
  receipt_upload_id: string;
  /**
   * Optional, and omitted rather than sent as null when nothing is chosen —
   * absent and explicit-null are different to the API (`'ticket_id' in body`).
   * Must be a ticket on `job_id`; the API 400s otherwise.
   */
  ticket_id?: string | null;
  /**
   * The meeting this receipt was for (CRM-API-2). General receipts only from
   * this app; the API requires a confirmed meeting of the same customer and,
   * when the expense names no customer, takes the meeting's.
   */
  sales_activity_id?: string;
}

/**
 * The optional body of `POST /expenses/:id/approve`. Approve has never taken
 * one, so send NO body at all when the ticket is unchanged: an absent key is
 * "leave it alone", and `ticket_id: null` is "clear it".
 */
export interface ExpenseApproveBody {
  ticket_id: string | null;
}

// ── The tickets on a job ─────────────────────────────────────────────────────

/**
 * One option in the ticket picker.
 *
 * Fed by `GET /tickets?jobId=` — there is no expenses-side endpoint for this
 * and there does not need to be one. That route is company-scoped, excludes
 * soft-deleted and archived rows, and has its own field_worker branch, so a
 * hand is offered the tickets they are actually on. **The param is camelCase
 * only**: `job_id` is ignored there and would return every open ticket in the
 * tenant, which is precisely the wrong-job link the API's 400 exists to stop.
 */
export interface JobTicketOption {
  id: string;
  ticketNumber: string | null;
}

/** Rows come back as `t.*` — snake_case, `ticket_number`. */
export function parseJobTicketOptions(body: unknown): JobTicketOption[] {
  const rows = unwrapArray<unknown>(body);
  const out: JobTicketOption[] = [];
  for (const raw of rows) {
    if (!raw || typeof raw !== 'object') continue;
    const r = raw as Record<string, unknown>;
    const id = str(r.id);
    if (!id) continue;
    out.push({ id, ticketNumber: str(pick(r, 'ticketNumber', 'ticket_number')) });
  }
  return out;
}

/**
 * What a ticket is called in a picker or on a row.
 *
 * `tickets.ticket_number` is NOT NULL, so the fallback is for a payload that
 * lost the column, not for a real ticket without a number. It falls back to a
 * fragment of the uuid rather than to `—`: an option a person cannot tell
 * apart is worse than an ugly one, and a blank option is unpickable.
 */
export function ticketOptionLabel(t: JobTicketOption): string {
  return t.ticketNumber ?? `Ticket ${t.id.slice(0, 8)}`;
}

// ── Approved cost summary — GET /expenses/summary (api#1023) ─────────────────

export type SummaryGroupBy = 'job' | 'customer' | 'user';

export const SUMMARY_GROUP_BY_LABELS: Record<SummaryGroupBy, string> = {
  job: 'By job',
  customer: 'By customer',
  user: 'By person',
};

/**
 * The six figures every group carries, AS THE API SENT THEM.
 *
 * Absent stays `null` and renders `—` — never 0. The API states the identity
 * net_known + gst_recovered + gst_unknown_gross === gross; a 0 standing in for a
 * figure that did not arrive would make a group read as fully netted when it is
 * not. Nothing here recomputes a net: the screen renders these verbatim.
 */
export interface CostFigures {
  count: number | null;
  gross: number | null;
  netKnown: number | null;
  gstRecovered: number | null;
  gstUnknownCount: number | null;
  gstUnknownGross: number | null;
}

export interface CostGroup {
  key: string;
  label: string;
  /** The job, customer or person was deleted since; still cost, so flagged not dropped. */
  deleted: boolean;
  figures: CostFigures;
}

export interface CostSummary {
  groupBy: SummaryGroupBy;
  totals: CostFigures;
  groups: CostGroup[];
  /**
   * The group with no job (by job) or no customer (by customer). ALWAYS present
   * for those two modes, even at zero — the API sends it that way so general
   * expenses are never silently dropped. Null only for "by person", which has
   * no such group.
   */
  unassigned: CostGroup | null;
  notes: { netBasis: string | null; grossVsNet: string | null; gstUnknown: string | null; usSalesTax: string | null };
}

function figuresOf(raw: unknown): CostFigures {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const num = (k: string): number | null => {
    const v = r[k];
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  return {
    count: num('expense_count'),
    gross: num('gross'),
    netKnown: num('net_known'),
    gstRecovered: num('gst_recovered'),
    gstUnknownCount: num('gst_unknown_count'),
    gstUnknownGross: num('gst_unknown_gross'),
  };
}

export function parseCostSummary(body: unknown, groupBy: SummaryGroupBy): CostSummary {
  const d = (unwrap<unknown>(body) ?? {}) as Record<string, any>;
  const listKey = groupBy === 'customer' ? 'customers' : groupBy === 'user' ? 'users' : 'jobs';
  const rows: any[] = Array.isArray(d[listKey]) ? d[listKey] : [];
  const groups: CostGroup[] = rows.map((g) => {
    if (groupBy === 'customer') {
      return { key: String(g.customer_id), label: str(g.customer_name) ?? String(g.customer_id), deleted: g.customer_deleted === true, figures: figuresOf(g) };
    }
    if (groupBy === 'user') {
      return { key: String(g.user_id), label: str(g.worker_name) ?? String(g.user_id), deleted: false, figures: figuresOf(g) };
    }
    return { key: String(g.job_id), label: str(g.job_number) ?? String(g.job_id), deleted: g.job_deleted === true, figures: figuresOf(g) };
  });
  const unassignedRaw = groupBy === 'job' ? d.no_job : groupBy === 'customer' ? d.no_customer : undefined;
  const unassigned = unassignedRaw && typeof unassignedRaw === 'object'
    ? { key: groupBy === 'job' ? 'no-job' : 'no-customer', label: groupBy === 'job' ? 'No job (General)' : 'No customer', deleted: false, figures: figuresOf(unassignedRaw) }
    : null;
  const n = (d.notes ?? {}) as Record<string, unknown>;
  return {
    groupBy,
    totals: figuresOf(d.totals),
    groups,
    unassigned,
    notes: { netBasis: str(n.net_basis), grossVsNet: str(n.gross_vs_net), gstUnknown: str(n.gst_unknown), usSalesTax: str(n.us_sales_tax) },
  };
}


// ── Reimbursement path, as the office reads it (api#1024 / FE-5) ─────────────

/**
 * "Payroll" / "Report — owed" / "Report — reimbursed <day>" / "Company-paid", or
 * null when no path is stamped yet (pending, rejected). `reimbursedDay` is the
 * tenant-local day, resolved by the caller (or by the API, on the report).
 */
export function reimbursementPathLabel(
  path: string | null | undefined,
  reimbursed: boolean,
  reimbursedDay: string | null,
): string | null {
  if (path === 'payroll') return 'Payroll';
  if (path === 'none') return 'Company-paid';
  // `reimbursed` decides, never the day: a mark whose day could not be read is
  // still a mark, and must not fall back to reading as owed.
  if (path === 'report') return reimbursed ? `Report — reimbursed${reimbursedDay ? ` ${reimbursedDay}` : ''}` : 'Report — owed';
  return null;
}

// ── The monthly reimbursement report — GET /expenses/report (api#1030) ──────

export type ReportBucket = 'owed' | 'reimbursed' | 'payroll' | 'company' | 'pending' | 'rejected';
export const REPORT_BUCKETS: readonly ReportBucket[] = ['owed', 'reimbursed', 'payroll', 'company', 'pending', 'rejected'];
export const REPORT_BUCKET_LABELS: Record<ReportBucket, string> = {
  owed: 'Owed',
  reimbursed: 'Reimbursed',
  payroll: 'Payroll',
  company: 'Company-paid',
  pending: 'Pending',
  rejected: 'Rejected',
};

export interface ReportRow {
  id: string;
  expenseDate: string | null;
  vendor: string | null;
  category: string | null;
  expenseKind: string | null;
  jobNumber: string | null;
  customerName: string | null;
  status: string;
  paidBy: string | null;
  reimbursementPath: string | null;
  bucket: ReportBucket | null;
  amount: number | null;
  gst: number | null;
  reimbursedOn: string | null;
  receiptUrl: string | null;
}

export interface PersonReport {
  month: string;
  monthLabel: string | null;
  currency: string | null;
  person: { id: string; name: string | null } | null;
  rows: ReportRow[];
  /** The ONE figure the office pays out. Rendered from the API; never summed here. */
  amountOwed: CostFigures;
  byBucket: Record<ReportBucket, CostFigures>;
  gstUnknownNote: string | null;
}

export interface RollUpPerson {
  userId: string;
  name: string | null;
  owed: CostFigures;
  reimbursed: CostFigures;
  payroll: CostFigures;
  company: CostFigures;
  pending: CostFigures;
}

export interface ReportRollUp {
  month: string;
  monthLabel: string | null;
  currency: string | null;
  people: RollUpPerson[];
  amountOwed: CostFigures;
}

const bucketOf = (v: unknown): ReportBucket | null =>
  typeof v === 'string' && (REPORT_BUCKETS as readonly string[]).includes(v) ? (v as ReportBucket) : null;

export function parsePersonReport(body: unknown): PersonReport | null {
  const d = unwrap<unknown>(body) as Record<string, any> | null;
  if (!d || typeof d !== 'object' || !str(d.month)) return null;
  const t = (d.totals ?? {}) as Record<string, any>;
  const rows: any[] = Array.isArray(d.rows) ? d.rows : [];
  return {
    month: str(d.month)!,
    monthLabel: str(d.month_label),
    currency: str(d.currency),
    person: d.user && typeof d.user === 'object' ? { id: String(d.user.id), name: str(d.user.name) } : null,
    rows: rows.map((r) => ({
      id: String(r.id),
      expenseDate: str(r.expense_date),
      vendor: str(r.vendor),
      category: str(r.category),
      expenseKind: str(r.expense_kind),
      jobNumber: str(r.job_number),
      customerName: str(r.customer_name),
      status: str(r.status) ?? '',
      paidBy: str(r.paid_by),
      reimbursementPath: str(r.reimbursement_path),
      bucket: bucketOf(r.bucket),
      amount: parseMoney(r.amount),
      gst: parseMoney(r.gst_amount),
      reimbursedOn: str(r.reimbursed_on),
      receiptUrl: str(r.receipt_url),
    })),
    amountOwed: figuresOf(t.amount_owed),
    byBucket: Object.fromEntries(REPORT_BUCKETS.map((k) => [k, figuresOf(t.by_bucket?.[k])])) as Record<ReportBucket, CostFigures>,
    gstUnknownNote: str(d.notes?.gst_unknown),
  };
}

export function parseReportRollUp(body: unknown): ReportRollUp | null {
  const d = unwrap<unknown>(body) as Record<string, any> | null;
  if (!d || typeof d !== 'object' || !str(d.month)) return null;
  const people: any[] = Array.isArray(d.people) ? d.people : [];
  return {
    month: str(d.month)!,
    monthLabel: str(d.month_label),
    currency: str(d.currency),
    people: people.map((p) => ({
      userId: String(p.user_id),
      name: str(p.worker_name),
      owed: figuresOf(p.owed),
      reimbursed: figuresOf(p.reimbursed),
      payroll: figuresOf(p.payroll),
      company: figuresOf(p.company),
      pending: figuresOf(p.pending),
    })),
    amountOwed: figuresOf(d.totals?.amount_owed),
  };
}

/** 'YYYY-MM' shifted by whole months, built from the parts — no Date, no zone. */
export function shiftMonth(month: string, delta: number): string {
  const m = /^(\d{4})-(\d{2})$/.exec(month);
  if (!m) return month;
  const index = Number(m[1]) * 12 + (Number(m[2]) - 1) + delta;
  const year = Math.floor(index / 12);
  const mon = (index % 12) + 1;
  return `${year}-${String(mon).padStart(2, '0')}`;
}
